import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { api, type ListResponse } from "@/api/client"
import type { TaskDetail } from "@/api/generated/TaskDetail"
import type { TaskSummary } from "@/api/generated/TaskSummary"
import type { TaskWorkers } from "@/api/generated/TaskWorkers"
import { useApiMutation } from "@/api/mutation"

/** Board lanes, in display order (mirrors `STATUSES` in `mit-tasks/src/model.rs`). */
export const TASK_STATUSES = ["backlog", "todo", "blocked", "progress", "review", "done"] as const
export type TaskStatus = (typeof TASK_STATUSES)[number]

/** Values accepted by the list `status` filter: lanes plus derived display states. */
export const TASK_STATUS_FILTERS = [...TASK_STATUSES, "waiting", "failed", "cancelled"] as const
export type TaskStatusFilter = (typeof TASK_STATUS_FILTERS)[number]

export const TASK_PRIORITIES = ["urgent", "high", "normal", "low"] as const
export type TaskPriority = (typeof TASK_PRIORITIES)[number]

export const TASK_CLOSURES = ["success", "failed", "cancelled"] as const
export type TaskClosure = (typeof TASK_CLOSURES)[number]

export const TASK_ORDERS = ["sort_order", "updated_at", "created_at", "due_at", "priority"] as const
export type TaskOrder = (typeof TASK_ORDERS)[number]

export interface TaskFilters {
  status?: string
  project_id?: number
  /** Only tasks without a project. */
  standalone?: boolean
  assignee_kind?: string
  assignee_id?: number
  priority?: string
  q?: string
  label?: string
  order_by?: TaskOrder
  limit?: number
  offset?: number
}

export const taskKeys = {
  all: ["tasks"] as const,
  lists: () => [...taskKeys.all, "list"] as const,
  list: (f: TaskFilters) => [...taskKeys.lists(), f] as const,
  details: () => [...taskKeys.all, "detail"] as const,
  detail: (id: number) => [...taskKeys.details(), id] as const,
  workers: () => [...taskKeys.all, "workers"] as const,
}

const listParams = (f: TaskFilters) => ({ ...f, standalone: f.standalone ? "true" : undefined })

export const tasksQuery = (f: TaskFilters) =>
  queryOptions({
    queryKey: taskKeys.list(f),
    queryFn: ({ signal }) => api.get<ListResponse<TaskSummary>>("/tasks", { signal, query: listParams(f) }),
    placeholderData: keepPreviousData,
  })

export const useTasks = (f: TaskFilters, opts: { refetchInterval?: number | false } = {}) =>
  useQuery({ ...tasksQuery(f), refetchInterval: opts.refetchInterval })

export const taskQuery = (id: number) =>
  queryOptions({
    queryKey: taskKeys.detail(id),
    queryFn: ({ signal }) => api.get<TaskDetail>(`/tasks/${id}`, { signal }),
  })

export const useTask = (
  id: number | undefined,
  opts: { refetchInterval?: ReturnType<typeof taskQuery>["refetchInterval"] } = {},
) => useQuery({ ...taskQuery(id ?? 0), enabled: id != null && id > 0, refetchInterval: opts.refetchInterval })

/**
 * Agents and teams a task can be assigned to (also the orchestrator's agent picker:
 * `/agents` itself needs the management permission).
 */
export const taskWorkersQuery = () =>
  queryOptions({
    queryKey: taskKeys.workers(),
    queryFn: ({ signal }) => api.get<TaskWorkers>("/tasks/workers", { signal }),
    staleTime: 60_000,
  })

export const useTaskWorkers = () => useQuery(taskWorkersQuery())

// ------------------------------------------------------------------------------ cache helpers

/** Stores a fresh detail (every task action returns one) and patches the row in cached lists. */
export function writeTaskToCache(qc: QueryClient, detail: TaskDetail) {
  qc.setQueryData(taskKeys.detail(detail.id), detail)
  patchTaskLists(qc, (items) => items.map((t) => (t.id === detail.id ? { ...t, ...pickSummary(detail) } : t)))
}

/** Applies `fn` to the items of every cached task list. */
export function patchTaskLists(qc: QueryClient, fn: (items: TaskSummary[]) => TaskSummary[]) {
  qc.setQueriesData<ListResponse<TaskSummary>>({ queryKey: taskKeys.lists() }, (old) =>
    old ? { ...old, items: fn(old.items) } : old,
  )
}

const SUMMARY_FIELDS = [
  "id",
  "number",
  "title",
  "status",
  "display_status",
  "has_pending_human_request",
  "closure_kind",
  "priority",
  "due_at",
  "sort_order",
  "auto_start",
  "progress_percent",
  "created_by_kind",
  "created_by_label",
  "project_id",
  "project_name",
  "checklist",
  "labels",
  "assignee_kind",
  "assignee_id",
  "assignee_name",
  "can_start",
  "can_retry",
  "is_executing",
  "created_at",
  "updated_at",
] as const satisfies readonly (keyof TaskSummary)[]

export function pickSummary(d: TaskDetail): TaskSummary {
  const out = {} as Record<string, unknown>
  for (const k of SUMMARY_FIELDS) out[k] = d[k]
  return out as TaskSummary
}

// ------------------------------------------------------------------------------ mutations

export interface TaskInput {
  title?: string
  description?: string
  expected_output?: string
  priority?: string
  project_id?: number | null
  due_at?: string | null
  auto_start?: boolean
  labels?: string[]
  checklist?: TaskSummary["checklist"]
  input_spec?: TaskDetail["input_spec"]
  progress_percent?: number | null
  depends_on?: number[]
}

export interface CreateTaskInput extends TaskInput {
  title: string
  input_values?: Record<string, unknown>
  assignee_kind?: string
  assignee_id?: number
}

/** Shared options for actions that return the updated task. */
function useTaskAction<TVars>(
  mutationFn: (vars: TVars) => Promise<TaskDetail>,
  opts: {
    successToast?: (d: TaskDetail, v: TVars) => string | false
    errorToast?: boolean | "unlessFields"
  } = {},
) {
  const qc = useQueryClient()
  return useApiMutation<TaskDetail, TVars>({
    mutationFn,
    onSuccess: (d) => writeTaskToCache(qc, d),
    invalidates: [taskKeys.lists()],
    successToast: opts.successToast,
    errorToast: opts.errorToast,
  })
}

export function useCreateTask() {
  const { t } = useTranslation()
  return useTaskAction((body: CreateTaskInput) => api.post<TaskDetail>("/tasks", body), {
    successToast: (d) => t("tasks.task.toast.created", { title: d.title }),
    errorToast: "unlessFields",
  })
}

export function useUpdateTask() {
  return useTaskAction(
    ({ id, ...body }: { id: number } & TaskInput) => api.patch<TaskDetail>(`/tasks/${id}`, body),
    {
      errorToast: "unlessFields",
    },
  )
}

/** Replaces the tasks this one waits for (a cycle is refused with a toast). */
export function useSetTaskDependencies() {
  return useTaskAction(({ id, depends_on }: { id: number; depends_on: number[] }) =>
    api.patch<TaskDetail>(`/tasks/${id}`, { depends_on }),
  )
}

export function useDeleteTask() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<void, Pick<TaskSummary, "id" | "title">>({
    mutationFn: (task) => api.delete(`/tasks/${task.id}`),
    onSuccess: (_d, task) => {
      qc.removeQueries({ queryKey: taskKeys.detail(task.id) })
      patchTaskLists(qc, (items) => items.filter((x) => x.id !== task.id))
    },
    invalidates: [taskKeys.lists()],
    successToast: (_d, task) => t("tasks.task.toast.deleted", { title: task.title }),
  })
}

export interface AssignInput {
  id: number
  assignee_kind: "agent" | "team" | "pool"
  assignee_id?: number
}

export function useAssignTask() {
  return useTaskAction(({ id, ...body }: AssignInput) => api.post<TaskDetail>(`/tasks/${id}/assign`, body))
}

export function useSubmitTaskInputs() {
  const { t } = useTranslation()
  return useTaskAction(
    ({ id, input_values }: { id: number; input_values: Record<string, unknown> }) =>
      api.post<TaskDetail>(`/tasks/${id}/inputs`, { input_values }),
    { successToast: () => t("tasks.task.toast.inputsSaved") },
  )
}

export function useStartTask() {
  const { t } = useTranslation()
  return useTaskAction((id: number) => api.post<TaskDetail>(`/tasks/${id}/start`), {
    successToast: () => t("tasks.task.toast.started"),
  })
}

export function useRetryTask() {
  const { t } = useTranslation()
  return useTaskAction((id: number) => api.post<TaskDetail>(`/tasks/${id}/retry`), {
    successToast: () => t("tasks.task.toast.retried"),
  })
}

export function useApproveTask() {
  const { t } = useTranslation()
  return useTaskAction(
    ({ id, message }: { id: number; message?: string }) =>
      api.post<TaskDetail>(`/tasks/${id}/approve`, message ? { message } : {}),
    { successToast: () => t("tasks.task.toast.approved") },
  )
}

export interface TaskMessageInput {
  id: number
  text: string
  decision?: "approve" | "deny"
  request_id?: number
}

/** Answers a pending question, decides a pending approval, or sends a follow-up. */
export function useSendTaskMessage() {
  const { t } = useTranslation()
  return useTaskAction(
    ({ id, ...body }: TaskMessageInput) => api.post<TaskDetail>(`/tasks/${id}/messages`, body),
    { successToast: () => t("tasks.task.toast.messageSent") },
  )
}

export function useCancelTask() {
  const { t } = useTranslation()
  return useTaskAction((id: number) => api.post<TaskDetail>(`/tasks/${id}/cancel`), {
    successToast: () => t("tasks.task.toast.cancelled"),
  })
}

export function useAddTaskComment() {
  return useTaskAction(({ id, text }: { id: number; text: string }) =>
    api.post<TaskDetail>(`/tasks/${id}/comments`, { text }),
  )
}

/** Writes a key/value into the task's project data store (project tasks only). */
export function useSetTaskOutput() {
  const { t } = useTranslation()
  return useTaskAction(
    ({ id, key, value }: { id: number; key: string; value: string }) =>
      api.put<TaskDetail>(`/tasks/${id}/outputs`, { key, value }),
    {
      successToast: (_d, v) => t("tasks.task.toast.outputSaved", { key: v.key }),
      errorToast: "unlessFields",
    },
  )
}

export function useMarkTaskReady() {
  return useTaskAction((id: number) => api.post<TaskDetail>(`/tasks/${id}/mark-ready`))
}

export interface TransitionInput {
  id: number
  status: TaskStatus
  reason?: string
  closure_kind?: TaskClosure
}

export function useTransitionTask() {
  return useTaskAction(({ id, ...body }: TransitionInput) =>
    api.post<TaskDetail>(`/tasks/${id}/transition`, body),
  )
}

export interface ReorderInput {
  id: number
  status?: TaskStatus
  after_id?: number | null
  before_id?: number | null
}

export const reorderTask = ({ id, ...body }: ReorderInput) =>
  api.post<TaskDetail>(`/tasks/${id}/reorder`, body)
export const transitionTask = ({ id, ...body }: TransitionInput) =>
  api.post<TaskDetail>(`/tasks/${id}/transition`, body)

/** Moves a task within (or into) a lane, relative to `after_id` / `before_id`. */
export function useReorderTask() {
  return useTaskAction(reorderTask)
}
